import { Trash } from "lucide-react";
import styles from "./action-bar.module.css";
import { PropsWithChildren, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import isNil from "lodash.isnil";
import { TextActions } from "../text-element-editor/text-actions";
import { TextItem, LayerItem } from "@exibra/shared";

/**
 * This is a component that allows you to edit the text of a text element in the canvas.
 */
interface ActionBarProps extends PropsWithChildren {
    /** The selected layer item. */
    selected?: LayerItem;
    /** The function to call when the layer item is deleted. */
    onDelete: (element: LayerItem) => void;
    /** The function to call when the layer item changes. */
    onChange: (element: LayerItem) => void;
    /** The function to call when the layer item is duplicated. */
    onDuplicate: (element: LayerItem) => void;
}

/**
 * This is a component that allows you to edit the text of a text element in the canvas.
 */
export const ActionBar: React.FC<ActionBarProps> = ({
    selected,
    onDelete,
    onChange,
    onDuplicate,
}) => {
    const actionBarRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    return (
        <div className={styles.actionBar} ref={actionBarRef}>
            {!isNil(selected) && (
                <>
                    {selected.type === "text" && (
                        <TextActions
                            element={selected as TextItem}
                            onChange={onChange}
                            onDuplicate={onDuplicate}
                        />
                    )}
                    <div className={styles.separator}></div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(selected)}
                        tooltip={t("general.delete")}
                    >
                        <Trash size={12} color="hsl(var(--destructive))" />
                    </Button>
                </>
            )}
        </div>
    );
};
